import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Download,
  FileText,
  Table,
  Archive,
  Code,
  Copy,
  Check,
  WrapText,
  Loader2,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';
import { formatFileSize } from './attachmentUtils';
import { getPresignedDownloadUrl, invalidatePresignedUrl } from './attachmentCache';
import { DocxPreview } from './DocxPreview';
import { SpreadsheetPreview } from './SpreadsheetPreview';
import { ArchivePreview } from './ArchivePreview';

interface UniversalPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  url: string;
  fileSize?: number | undefined;
  workspaceId?: string | undefined;
  fileId?: string | undefined;
  category?: string | undefined;
}

export const UniversalPreviewModal: React.FC<UniversalPreviewModalProps> = ({
  isOpen,
  onClose,
  fileName,
  url,
  fileSize,
  workspaceId,
  fileId,
}) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isFetchingText, setIsFetchingText] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [wordWrap, setWordWrap] = useState<boolean>(true);

  const extension = fileName.includes('.')
    ? fileName.split('.').pop()?.toLowerCase() || ''
    : '';

  const isDocx = extension === 'docx';
  const isExcel = ['xlsx', 'xls'].includes(extension);
  const isZip = extension === 'zip';
  const isCsv = extension === 'csv';
  const isCodeOrText = [
    'txt',
    'log',
    'md',
    'sql',
    'js',
    'ts',
    'jsx',
    'tsx',
    'java',
    'py',
    'json',
    'xml',
    'yaml',
    'yml',
    'html',
    'css',
    'scss',
    'sh',
  ].includes(extension);

  const resolveUrl = useCallback(
    async (forceRefresh = false) => {
      if (!workspaceId || !fileId) {
        setResolvedUrl(url);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setIsError(false);
      try {
        if (forceRefresh) {
          invalidatePresignedUrl(workspaceId, fileId);
        }
        const data = await getPresignedDownloadUrl(workspaceId, fileId);
        if (data.url) {
          setResolvedUrl(data.url);
        } else {
          setIsError(true);
        }
      } catch (err) {
        console.warn('[UniversalPreviewModal] Failed to resolve presigned URL:', err);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [workspaceId, fileId, url]
  );

  useEffect(() => {
    if (isOpen) {
      resolveUrl(false);
    }
  }, [isOpen, resolveUrl]);

  // Fetch text content for text/code/csv preview
  useEffect(() => {
    let isMounted = true;
    if (isOpen && resolvedUrl && (isCodeOrText || isCsv)) {
      setIsFetchingText(true);
      fetch(resolvedUrl)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`);
          return res.text();
        })
        .then((text) => {
          if (isMounted) {
            if (text.length > 2 * 1024 * 1024) {
              setTextContent(text.slice(0, 2 * 1024 * 1024) + '\n\n... [Preview truncated due to 2MB limit. Download file for complete content.]');
            } else {
              setTextContent(text);
            }
          }
        })
        .catch((err) => {
          console.warn('[UniversalPreviewModal] Text fetch error:', err);
          if (isMounted) setTextContent(null);
        })
        .finally(() => {
          if (isMounted) setIsFetchingText(false);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, resolvedUrl, isCodeOrText, isCsv]);

  // Keyboard ESC listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const activeDownloadUrl = resolvedUrl || url;

  const handleCopyText = () => {
    if (textContent) {
      navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Parse CSV into table data safely
  const parseCsv = (raw: string) => {
    const lines = raw.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (lines.length === 0 || !lines[0]) return { headers: [], rows: [], totalCount: 0 };
    const parseLine = (line: string) => {
      const result: string[] = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(cur.trim());
          cur = '';
        } else {
          cur += char;
        }
      }
      result.push(cur.trim());
      return result;
    };
    const firstLine = lines[0];
    const headers = parseLine(firstLine);
    const rows = lines.slice(1, 1000).map((line) => parseLine(line));
    return { headers, rows, totalCount: lines.length - 1 };
  };

  const renderCsvTable = () => {
    if (!textContent) return null;
    const { headers, rows, totalCount } = parseCsv(textContent);

    return (
      <div className="flex flex-col h-full w-full select-text bg-slate-950 text-slate-200">
        <div className="flex-1 overflow-auto p-4">
          <table className="w-full text-xs border-collapse text-left">
            <thead>
              <tr className="bg-slate-900 border-b border-slate-800 text-slate-400 font-mono">
                <th className="p-2 border-r border-slate-800 w-12 text-center select-none">#</th>
                {headers.map((h, idx) => (
                  <th key={idx} className="p-2.5 font-semibold border-r border-slate-800 whitespace-nowrap">
                    {h || `Col ${idx + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx} className="border-b border-slate-800/60 hover:bg-slate-900/50 font-mono">
                  <td className="p-2 border-r border-slate-800 text-slate-500 text-center select-none text-[11px]">
                    {rIdx + 1}
                  </td>
                  {headers.map((_, cIdx) => (
                    <td key={cIdx} className="p-2.5 border-r border-slate-800/60 whitespace-nowrap truncate max-w-xs">
                      {row[cIdx] !== undefined ? row[cIdx] : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalCount > 999 && (
          <div className="p-2 text-[11px] text-amber-400 bg-amber-500/10 border-t border-slate-800 text-center font-medium">
            Showing first 1,000 rows. Download file for complete dataset.
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl h-[85vh] rounded-2xl bg-card border border-border shadow-2xl flex flex-col overflow-hidden select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              {isExcel || isCsv ? (
                <Table className="h-4.5 w-4.5 text-emerald-500" />
              ) : isZip ? (
                <Archive className="h-4.5 w-4.5 text-purple-500" />
              ) : isCodeOrText ? (
                <Code className="h-4.5 w-4.5 text-blue-500" />
              ) : (
                <FileText className="h-4.5 w-4.5 text-blue-500" />
              )}
            </div>
            <div className="min-w-0">
              <span className="font-bold text-xs sm:text-sm text-foreground truncate block max-w-md">
                {fileName}
              </span>
              <span className="text-[11px] text-muted-foreground font-medium">
                {extension.toUpperCase()} File • {formatFileSize(fileSize)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isCodeOrText && textContent && (
              <>
                <button
                  type="button"
                  onClick={() => setWordWrap(!wordWrap)}
                  className={`p-1.5 rounded-lg border transition ${
                    wordWrap
                      ? 'bg-primary/15 text-primary border-primary/30'
                      : 'hover:bg-muted text-muted-foreground border-transparent'
                  }`}
                  title="Toggle Word Wrap"
                >
                  <WrapText className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={handleCopyText}
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition border border-transparent"
                  title="Copy content"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </button>
              </>
            )}

            {resolvedUrl && (
              <a
                href={resolvedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
                title="Open raw stream in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}

            <a
              href={activeDownloadUrl}
              download={fileName}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs transition"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Download</span>
            </a>

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Modal Body Dispatcher */}
        <div className="flex-1 bg-slate-950 flex items-center justify-center overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-xs font-semibold">Resolving secure preview...</p>
            </div>
          ) : isError || !resolvedUrl ? (
            <div className="flex flex-col items-center justify-center p-6 text-center max-w-md gap-3">
              <AlertCircle className="h-10 w-10 text-amber-400" />
              <p className="font-bold text-foreground text-sm">Preview Unavailable</p>
              <p className="text-xs text-muted-foreground">
                Unable to load secure URL for this attachment. You can still download the original file.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => resolveUrl(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs transition"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Retry</span>
                </button>
                <a
                  href={activeDownloadUrl}
                  download={fileName}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs transition"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>Download File</span>
                </a>
              </div>
            </div>
          ) : isDocx ? (
            <DocxPreview url={resolvedUrl} fileName={fileName} />
          ) : isExcel ? (
            <SpreadsheetPreview url={resolvedUrl} fileName={fileName} />
          ) : isZip ? (
            <ArchivePreview url={resolvedUrl} fileName={fileName} />
          ) : isCsv ? (
            isFetchingText ? (
              <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                <p className="text-xs font-semibold">Parsing CSV dataset...</p>
              </div>
            ) : (
              renderCsvTable()
            )
          ) : isCodeOrText ? (
            isFetchingText ? (
              <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-xs font-semibold">Fetching text content...</p>
              </div>
            ) : (
              <div className="flex-1 w-full h-full p-4 overflow-auto font-mono text-xs text-slate-200 bg-slate-950 select-text leading-relaxed">
                <pre className={`whitespace-${wordWrap ? 'pre-wrap' : 'pre'} break-words`}>
                  {textContent || 'No text content found or empty file.'}
                </pre>
              </div>
            )
          ) : (
            /* File Info & Details View for Unsupported Binary Formats */
            <div className="flex flex-col items-center justify-center p-8 text-center max-w-lg mx-auto gap-4 select-text">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shadow-lg">
                <FileText className="h-8 w-8 text-blue-400" />
              </div>

              <div>
                <h3 className="text-base font-bold text-slate-100 truncate max-w-md">{fileName}</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {extension.toUpperCase()} File • {formatFileSize(fileSize)}
                </p>
              </div>

              <div className="w-full bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-left text-xs space-y-2.5 text-slate-300">
                <div className="flex justify-between border-b border-slate-800/80 pb-2">
                  <span className="text-slate-500 font-medium">File Format:</span>
                  <span className="font-mono font-semibold text-slate-200">{extension.toUpperCase()}</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/80 pb-2">
                  <span className="text-slate-500 font-medium">File Size:</span>
                  <span className="font-semibold text-slate-200">{formatFileSize(fileSize)}</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/80 pb-2">
                  <span className="text-slate-500 font-medium">Security Status:</span>
                  <span className="inline-flex items-center gap-1 text-emerald-400 font-semibold">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Verified & Encrypted
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-400 leading-normal max-w-sm">
                Inline browser content rendering for {extension.toUpperCase()} binary files is not supported. Click below to download original file.
              </p>

              <a
                href={activeDownloadUrl}
                download={fileName}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs shadow-md hover:bg-primary/90 transition mt-1"
              >
                <Download className="h-4 w-4" />
                <span>Download Original File</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

